import { PayloadAction } from '@reduxjs/toolkit';
import { call, CallEffect, put, PutEffect, SelectEffect, takeLatest } from 'redux-saga/effects';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import i18n from '../../i18n';
import {
  IGetBookings,
  IGetBookingsForSelectedMonth,
  fetchBookings,
  fetchBookingsFailure,
  fetchBookingsSuccess,
  fetchBookingsByMonth,
  fetchBookingsByMonthSuccess,
  fetchBookingsByMonthFailure,
  fetchCountryCode,
  fetchCountryCodeFailure,
  fetchCountryCodeSuccess,
  createCoachAvailability,
  ICoachAvailability,
  createCoachAvailabilitySuccess,
  createCoachAvailabilityFailure,
  getGoogleCalendarAuthURI,
  getGoogleCalendarAuthURISuccess,
  getGoogleCalendarAuthURIFailure,
  getOutlookCalendarAuthURI,
  getOutlookCalendarAuthURISuccess,
  getOutlookCalendarAuthURIFailure,
  saveLinkedCalendar,
  saveLinkedCalendarSuccess,
  saveLinkedCalendarFailure,
  ISaveCalendar,
  checkTokenExists,
  checkTokenExistsSuccess,
  checkTokenExistsFailure,
  ICoachOfficeDetails,
  createConsultationPreferenceSuccess,
  createConsultationPreferenceFailure,
  createConsultationPreference,
  IConsultationPreferenceInput,
  fetchCoachConfigurationsSuccess,
  fetchCoachConfigurationsFailure,
  fetchCoachConfigurations,
  saveOfficedetailsSuccess,
  saveOfficedetailsFailure,
  saveOfficedetails,
  saveBookingPageUrl,
  saveBookingPageUrlSuccess,
  saveBookingPageUrlFailure,
  fetchCoachAvailabilities,
  fetchCoachAvailabilitiesSuccess,
  fetchCoachAvailabilitiesFailure,
  deleteCalendar,
  deleteCalendarSuccess,
  deleteCalendarFailure,
  getZoomAuthURI,
  getZoomAuthURISuccess,
  getZoomAuthURIFailure,
  updateCoachAvailabilitySuccess,
  updateCoachAvailabilityFailure,
  updateCoachAvailability,
  checkUrlExistence,
  ICheckUrlExistenceResponse,
  checkUrlExistenceSuccess,
  checkUrlExistenceFailure,
  ICoachBookingPageUrl,
  getGoogleMeetAuthURI,
  getGoogleMeetAuthURISuccess,
  getGoogleMeetAuthURIFailure,
  checkVideoPlatformTokenExistsSuccess,
  checkVideoPlatformTokenExistsFailure,
  checkVideoPlatformTokenExists,
  deleteVideoPlatform,
  deleteVideoPlatformSuccess,
  deleteVideoPlatformFailure,
  ISaveVideoPlatform,
  saveLinkedVideoPlatformSuccess,
  saveLinkedVideoCallPlatformFailure,
  saveLinkedVideoPlatform,
  cancelBooking,
  IBooking,
  cancelBookingSuccess,
  cancelBookingFailure,
  updateBooking,
  ICoachUpdateBookingData,
  updateBookingSuccess,
  updateBookingFailure,
  getRescheduleTimesRequestSuccess,
  getRescheduleTimesRequestFailure,
  getRescheduleTimesRequest,
  resetCalendarAuthURI,
  resetVideoPlatforAuthURI,
  updateCoachActiveStepSuccess,
  updateCoachActiveStepFailure,
  updateCoachActiveStep,
  VideoCallPlatform,
  ICalendarData,
  IVideoPlatformData,
  saveGeneralSettings,
} from './bookingSlice';
import client from '../../service/graphql';
import {
  CHECK_TOKEN_EXISTS,
  CREATE_COACH_AVAILABILITY,
  GET_BOOKINGS,
  GET_BOOKINGS_FOR_MONTH,
  GET_GOOGLE_CALENDAR_AUTH_URI,
  GET_OUTLOOK_CALENDAR_AUTH_URI,
  SAVE_COACH_OFFICE_DETAILS,
  SAVE_LINKED_CALENDAR,
  GET_COACH_CONFIGURATIONS,
  SAVE_CONSULTATION_PREFERENCE,
  GET_COACH_AVAILABILITIES,
  DELETE_CALENDAR,
  GET_ZOOM_AUTH_URI,
  UPDATE_COACH_AVAILABILITY,
  DELETE_TOKENS,
  CHECK_URL_EXISTENCE,
  SAVE_BOOKING_PAGE_URL,
  SAVE_VIDEOCALL_PLATFORM,
  CANCEL_BOOKING,
  GET_RESCHEDULE_TIME_SLOTS,
  UPDATE_BOOKING,
  UPDATE_COACH_ACTIVE_STEP,
} from '../../service/graphql/bookingQuery';
import getCountryCode, { ICountryCodeReponse } from '../../service/api/bookingApi';
import { toastStyles } from '../../utils/utils';
import { TokenTypeEnum } from '../../types/booking';
import { IGraphQLResponse } from '../../types/commonTypes';

export interface IChangeCoachStatusResponse {
  changeCoachStatus: {
    id: string;
    isActive: boolean;
  };
}

export interface IArchiveCoachPayload {
  coachId: string;
}

function* watchFetchBookings({ payload }: PayloadAction<IGetBookings>) {
  const { date, month, year } = payload;
  try {
    const response = yield client.query({
      query: GET_BOOKINGS,
      variables: { date, month, year },
    });
    yield put(fetchBookingsSuccess(response.data.getBookingsByDate));
  } catch (error: unknown) {
    yield put(fetchBookingsFailure());
    if (error instanceof Error) {
      yield put(fetchBookingsFailure());
    }
  }
}

function* watchFetchBookingsByMonth({ payload }: PayloadAction<IGetBookingsForSelectedMonth>) {
  const { month, year } = payload;
  try {
    const response = yield client.query({
      query: GET_BOOKINGS_FOR_MONTH,
      variables: { month, year },
    });
    yield put(fetchBookingsByMonthSuccess(response.data.getBookedDatesPerMonth));
  } catch (error) {
    if (error instanceof Error) {
      yield put(fetchBookingsByMonthFailure());
    }
  }
}

function* watchFetchCountryCode(): Generator<CallEffect | PutEffect, void, ICountryCodeReponse> {
  try {
    const response = yield call(getCountryCode);
    const data = yield response.json();
    if (response.ok && data.country) {
      yield put(fetchCountryCodeSuccess(data.country));
    }
  } catch (error: unknown) {
    if (error instanceof Error) yield put(fetchCountryCodeFailure());
  }
}

function* watchCreateCoachAvailability({ payload }: PayloadAction<ICoachAvailability>) {
  try {
    yield call(client.mutate.bind(client), {
      mutation: CREATE_COACH_AVAILABILITY,
      variables: { ...payload },
    });
    yield put(createCoachAvailabilitySuccess(payload));
  } catch (error: unknown) {
    if (error instanceof Error) {
      toast.error(error.message, toastStyles.error);
      yield put(createCoachAvailabilityFailure());
    }
  }
}

function* watchUpdateCoachAvailability({ payload }: PayloadAction<ICoachAvailability>) {
  try {
    const response = yield call(client.mutate.bind(client), {
      mutation: UPDATE_COACH_AVAILABILITY,
      variables: { ...payload },
    });
    yield put(updateCoachAvailabilitySuccess({ ...payload }));
    toast.success(response.data.updateCoachAvailability.message, toastStyles.success);
  } catch (error: unknown) {
    if (error instanceof Error) {
      toast.error(error.message, toastStyles.error);
      yield put(updateCoachAvailabilityFailure());
    }
  }
}

function* watchGetRescheduleTimesRequest({
  payload,
}: PayloadAction<{ bookingId: number; date: number; month: number; year: number }>) {
  const { bookingId, date, month, year } = payload;
  try {
    const result = yield call(client.query.bind(client), {
      query: GET_RESCHEDULE_TIME_SLOTS,
      variables: { bookingId, date, month, year },
    });
    yield put(getRescheduleTimesRequestSuccess(result.data.getRescheduleTimes));
  } catch (error) {
    if (error instanceof Error) {
      yield put(getRescheduleTimesRequestFailure(error.message));
    }
  }
}

function* watchGetGoogleCalendarAuthURI({ payload }: PayloadAction<IVideoPlatformData>) {
  try {
    if (payload?.videoCallPlatform === VideoCallPlatform.GOOGLE_MEET) {
      const data: ISaveCalendar = {
        coachId: payload.coachId,
        calendar: TokenTypeEnum.GOOGLE_CALENDER,
        calendarUsername: payload.videoCallPlatformUsername,
      };
      if (payload.activeStep) {
        data.activeStep = payload.activeStep;
      }
      yield put(saveLinkedCalendar(data));
    } else {
      const response = yield client.query({
        query: GET_GOOGLE_CALENDAR_AUTH_URI,
      });
      yield put(getGoogleCalendarAuthURISuccess(response.data.getGoogleCalendarAuthURI));
    }
  } catch (error: unknown) {
    yield put(getGoogleCalendarAuthURIFailure());
  }
}

function* watchGetGoogleMeetAuthURI({ payload }: PayloadAction<ICalendarData>) {
  try {
    if (payload?.calendar === TokenTypeEnum.GOOGLE_CALENDER) {
      const data: ISaveVideoPlatform = {
        coachId: payload.coachId,
        videoCallPlatform: VideoCallPlatform.GOOGLE_MEET,
        videoCallPlatformUsername: payload.calendarUsername,
      };
      if (payload.activeStep) {
        data.activeStep = payload.activeStep;
      }
      yield put(saveLinkedVideoPlatform(data));
    } else {
      const response = yield client.query({
        query: GET_GOOGLE_CALENDAR_AUTH_URI,
      });
      yield put(getGoogleMeetAuthURISuccess(response.data.getGoogleCalendarAuthURI));
    }
  } catch (error: unknown) {
    yield put(getGoogleMeetAuthURIFailure());
  }
}

function* watchGetOutlookCalendarAuthURI() {
  try {
    const response = yield client.query({
      query: GET_OUTLOOK_CALENDAR_AUTH_URI,
    });
    yield put(getOutlookCalendarAuthURISuccess(response.data.getOutLookCalendarAuthURI));
  } catch (error: unknown) {
    yield put(getOutlookCalendarAuthURIFailure());
  }
}

function* watchSaveLinkedCalendar({ payload }: PayloadAction<ISaveCalendar>) {
  try {
    const response = yield call(client.mutate.bind(client), {
      mutation: SAVE_LINKED_CALENDAR,
      variables: { ...payload },
    });
    yield put(saveLinkedCalendarSuccess(response.data.saveCalendar));
  } catch (error: unknown) {
    if (error instanceof Error) {
      toast.error(error.message, toastStyles.error);
      yield put(saveLinkedCalendarFailure());
    }
  }
}

function* watchSaveLinkedVideoCallPlatform({ payload }: PayloadAction<ISaveVideoPlatform>) {
  try {
    const response = yield call(client.mutate.bind(client), {
      mutation: SAVE_VIDEOCALL_PLATFORM,
      variables: { ...payload },
    });
    yield put(saveLinkedVideoPlatformSuccess(response.data.saveVideoCallPlatForm));
  } catch (error: unknown) {
    if (error instanceof Error) {
      toast.error(error.message, toastStyles.error);
      yield put(saveLinkedVideoCallPlatformFailure());
    }
  }
}

function* watchCheckTokenExists({
  payload,
}: PayloadAction<{ coachId: string; tokenType: TokenTypeEnum }>) {
  try {
    const response = yield client.query({
      query: CHECK_TOKEN_EXISTS,
      variables: { ...payload },
    });
    // throw new Error
    yield put(checkTokenExistsSuccess(response.data.checkTokenExists));
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(checkTokenExistsFailure());
      yield put(resetCalendarAuthURI());
    }
  }
}
function* watchCheckVideoPlatFormTokenExists({
  payload,
}: PayloadAction<{ coachId: string; tokenType: TokenTypeEnum }>) {
  try {
    const response = yield client.query({
      query: CHECK_TOKEN_EXISTS,
      variables: { ...payload },
    });
    yield put(checkVideoPlatformTokenExistsSuccess(response.data.checkTokenExists));
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(checkVideoPlatformTokenExistsFailure());
      yield put(resetVideoPlatforAuthURI());
    }
  }
}

function* watchCreateConsultationPreference({
  payload,
}: PayloadAction<IConsultationPreferenceInput>) {
  try {
    const response = yield call(client.mutate.bind(client), {
      mutation: SAVE_CONSULTATION_PREFERENCE,
      variables: { ...payload },
    });
    yield put(createConsultationPreferenceSuccess(response.data.saveConsultationPreference));
  } catch (error: unknown) {
    if (error instanceof Error) {
      toast.error(error.message, toastStyles.error);
      yield put(createConsultationPreferenceFailure());
    }
  }
}

function* watchFetchCoachConfigurations({ payload }: PayloadAction<{ coachId: string }>) {
  try {
    const result = yield call(client.query.bind(client), {
      query: GET_COACH_CONFIGURATIONS,
      variables: { coachId: payload.coachId },
    });
    yield put(fetchCoachConfigurationsSuccess(result.data.getCoachConfigurations));
  } catch (error: unknown) {
    yield put(fetchCoachConfigurationsFailure());
  }
}

function* watchSaveOfficeDetails({ payload }: PayloadAction<ICoachOfficeDetails>) {
  try {
    yield call(client.mutate.bind(client), {
      mutation: SAVE_COACH_OFFICE_DETAILS,
      variables: { ...payload },
    });
    if (payload.isGeneralSettings) {
      yield put(saveGeneralSettings({ ...payload }));
      toast.success(i18n.t('bookings.officeDetailsSaved'), toastStyles.success);
    } else {
      yield put(saveOfficedetailsSuccess({ ...payload }));
    }
  } catch (error: unknown) {
    yield put(saveOfficedetailsFailure());
  }
}

function* watchSaveBookingPageUrl({ payload }: PayloadAction<ICoachBookingPageUrl>) {
  try {
    yield call(client.mutate.bind(client), {
      mutation: SAVE_BOOKING_PAGE_URL,
      variables: { ...payload },
    });
    yield put(saveBookingPageUrlSuccess(payload.url));
  } catch (error: unknown) {
    if (error instanceof Error) {
      toast.error(error.message, toastStyles.error);
      yield put(saveBookingPageUrlFailure());
    }
  }
}

function* watchFetchCoachAvailabilities({ payload }: PayloadAction<{ coachId: string }>) {
  try {
    const result = yield call(client.query.bind(client), {
      query: GET_COACH_AVAILABILITIES,
      variables: { coachId: payload.coachId },
    });
    yield put(fetchCoachAvailabilitiesSuccess(result.data.findCoachAvailabilities));
  } catch (error: unknown) {
    if (error instanceof Error) {
      toast.error(error.message, toastStyles.error);
      yield put(fetchCoachAvailabilitiesFailure());
    }
  }
}

function* watchdDeleteCalendar({
  payload,
}: PayloadAction<{ calenderType: TokenTypeEnum; isConfigured: boolean }>) {
  try {
    if (payload.isConfigured) {
      yield call(client.mutate.bind(client), {
        mutation: DELETE_TOKENS,
        variables: {
          tokenType: payload.calenderType,
          clearCalendar: true,
          clearVideoPlatform: false,
        },
      });
    } else {
      yield call(client.mutate.bind(client), {
        mutation: DELETE_CALENDAR,
        variables: { ...payload },
      });
    }
    yield put(deleteCalendarSuccess());
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(deleteCalendarFailure());
    }
  }
}

function* watchdDeleteVideoPlatform({
  payload,
}: PayloadAction<{ calenderType: TokenTypeEnum; isConfigured: boolean }>) {
  try {
    if (payload.isConfigured) {
      yield call(client.mutate.bind(client), {
        mutation: DELETE_TOKENS,
        variables: {
          tokenType: payload.calenderType,
          clearCalendar: false,
          clearVideoPlatform: true,
        },
      });
    } else {
      yield call(client.mutate.bind(client), {
        mutation: DELETE_CALENDAR,
        variables: { ...payload },
      });
    }
    yield put(deleteVideoPlatformSuccess());
  } catch (error: unknown) {
    if (error instanceof Error) {
      toast.error(error.message, toastStyles.error);
      yield put(deleteVideoPlatformFailure());
    }
  }
}

function* watchGetZoomAuthURI() {
  try {
    const response = yield client.query({
      query: GET_ZOOM_AUTH_URI,
    });
    yield put(getZoomAuthURISuccess(response.data.getZoomAuthURI));
  } catch (error: unknown) {
    yield put(getZoomAuthURIFailure());
  }
}

function* watchCheckUrlExistence({
  payload,
}: PayloadAction<string>): Generator<
  CallEffect | PutEffect | SelectEffect,
  void,
  IGraphQLResponse<ICheckUrlExistenceResponse>
> {
  try {
    const result: IGraphQLResponse<ICheckUrlExistenceResponse> = yield call(
      client.query.bind(client),
      {
        query: CHECK_URL_EXISTENCE,
        variables: {
          checkUrlExistenceArgs: {
            url: payload,
          },
        },
      }
    );

    yield put(checkUrlExistenceSuccess(result.data.checkUrlExistence.success));
  } catch (error) {
    if (error instanceof Error) yield put(checkUrlExistenceFailure(error?.message));
  }
}

function* watchCancelBooking({ payload }: PayloadAction<IBooking>) {
  try {
    const res = yield call(client.mutate.bind(client), {
      mutation: CANCEL_BOOKING,
      variables: { id: payload.id },
    });
    yield put(cancelBookingSuccess(res.data.cancelBooking));
    toast.error(i18n.t('bookings.cancelledSession'), toastStyles.error);
  } catch (error: unknown) {
    if (error instanceof Error) {
      toast.error(error.message, toastStyles.error);
      yield put(cancelBookingFailure());
    }
  }
}

function* watchUpdateBooking({ payload }: PayloadAction<ICoachUpdateBookingData>) {
  try {
    const response = yield call(client.mutate.bind(client), {
      mutation: UPDATE_BOOKING,
      variables: {
        bookingId: payload.bookingId,
        servicePackageId: payload.servicePackageId,
        date: payload.date,
      },
    });
    yield put(updateBookingSuccess(response.data.rescheduleBooking));
    toast.success(i18n.t('bookings.bookingSettings.appointmentSuccess'), toastStyles.success);
    yield put(
      fetchBookingsByMonth({
        month: dayjs(payload.date).month(),
        year: dayjs(payload.date).year(),
      })
    );
  } catch (error: unknown) {
    if (error instanceof Error) {
      toast.error(error.message, toastStyles.error);
      yield put(updateBookingFailure());
    }
  }
}

function* watchUpdateCoachActiveStep({ payload }: PayloadAction<{ activeStep: number }>) {
  try {
    const response = yield call(client.mutate.bind(client), {
      mutation: UPDATE_COACH_ACTIVE_STEP,
      variables: { ...payload },
    });
    yield put(updateCoachActiveStepSuccess(response.data.updateCoachActiveStep.activeStep));
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(updateCoachActiveStepFailure());
    }
  }
}

export function* watchBookingSagas() {
  yield takeLatest(fetchBookings, watchFetchBookings);
  yield takeLatest(fetchBookingsByMonth, watchFetchBookingsByMonth);
  yield takeLatest(fetchCountryCode, watchFetchCountryCode);
  yield takeLatest(createCoachAvailability, watchCreateCoachAvailability);
  yield takeLatest(updateCoachAvailability, watchUpdateCoachAvailability);
  yield takeLatest(getGoogleCalendarAuthURI, watchGetGoogleCalendarAuthURI);
  yield takeLatest(getGoogleMeetAuthURI, watchGetGoogleMeetAuthURI);
  yield takeLatest(getOutlookCalendarAuthURI, watchGetOutlookCalendarAuthURI);
  yield takeLatest(saveLinkedCalendar, watchSaveLinkedCalendar);
  yield takeLatest(saveLinkedVideoPlatform, watchSaveLinkedVideoCallPlatform);
  yield takeLatest(checkTokenExists, watchCheckTokenExists);
  yield takeLatest(checkVideoPlatformTokenExists, watchCheckVideoPlatFormTokenExists);
  yield takeLatest(createConsultationPreference, watchCreateConsultationPreference);
  yield takeLatest(fetchCoachConfigurations, watchFetchCoachConfigurations);
  yield takeLatest(saveOfficedetails, watchSaveOfficeDetails);
  yield takeLatest(saveBookingPageUrl, watchSaveBookingPageUrl);
  yield takeLatest(fetchCoachAvailabilities, watchFetchCoachAvailabilities);
  yield takeLatest(getRescheduleTimesRequest, watchGetRescheduleTimesRequest);
  yield takeLatest(deleteCalendar, watchdDeleteCalendar);
  yield takeLatest(deleteVideoPlatform, watchdDeleteVideoPlatform);
  yield takeLatest(getZoomAuthURI, watchGetZoomAuthURI);
  yield takeLatest(checkUrlExistence, watchCheckUrlExistence);
  yield takeLatest(cancelBooking, watchCancelBooking);
  yield takeLatest(updateBooking, watchUpdateBooking);
  yield takeLatest(updateCoachActiveStep, watchUpdateCoachActiveStep);
}

const bookingSagas = watchBookingSagas;
export default bookingSagas;
